import { useEffect, useState } from "react";
import { Menu, X, ArrowRight, Sun, Moon, UserCircle } from "lucide-react";

const NAV = [
    { id: "quem-somos", label: "Quem Somos" },
    { id: "servicos", label: "Serviços" },
    { id: "cases", label: "Cases" },
    { id: "trabalhe-conosco", label: "Trabalhe Conosco" },
];

const PORTAL_URL = "https://hr-goodmax.emergent.host";

function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return;
    const paddingTop = parseFloat(getComputedStyle(el).paddingTop) || 0;
    const top = el.getBoundingClientRect().top + window.scrollY - 80 + paddingTop - 24;
    window.scrollTo({ top, behavior: "smooth" });
}

export default function Header({ theme, toggleTheme }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/80 dark:bg-black/75 backdrop-blur-xl border-b border-zinc-200/70 dark:border-white/5 py-2"
                    : "bg-transparent py-3"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between transition-all">
                <button
                    data-testid="header-logo-btn"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="flex items-center gap-3 group"
                >
                    <img
                        src="/goodmax-symbol.png?v=3"
                        alt="GOODMAX"
                        className={`w-auto transition-all duration-300 ${
                            scrolled ? "h-10 sm:h-12" : "h-12 sm:h-14"
                        }`}
                    />
                    <div className="flex flex-col leading-none">
                        <span className="font-heading font-black tracking-tight text-zinc-950 dark:text-white text-xl sm:text-2xl">
                            GOODMAX
                        </span>
                        <span className="text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400 mt-1">
                            Gestão de Serviços
                        </span>
                    </div>
                </button>

                <nav className="hidden md:flex items-center gap-1">
                    {NAV.map((n) => (
                        <button
                            key={n.id}
                            data-testid={`nav-${n.id}-btn`}
                            onClick={() => go(n.id)}
                            className="px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-white rounded-full transition-colors"
                        >
                            {n.label}
                        </button>
                    ))}
                    <a
                        data-testid="nav-portal-colaborador-btn"
                        href={PORTAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1 inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full border border-[#2DA89E]/40 text-[#2DA89E] hover:bg-[#2DA89E]/10 transition-colors"
                    >
                        <UserCircle className="w-4 h-4" />
                        Portal do Colaborador
                    </a>
                    <button
                        data-testid="theme-toggle-btn"
                        onClick={toggleTheme}
                        aria-label="Alternar tema"
                        className="ml-2 inline-flex items-center justify-center w-10 h-10 rounded-full border border-zinc-300/60 dark:border-white/10 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-white/5 transition-colors"
                    >
                        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </button>
                    <button
                        data-testid="nav-contato-btn"
                        onClick={() => go("contato")}
                        className="ml-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E89546] text-black font-semibold text-sm transition-transform hover:bg-[#f0a25b] active:scale-95"
                    >
                        Contato <ArrowRight className="w-4 h-4" />
                    </button>
                </nav>

                <div className="md:hidden flex items-center gap-2">
                    <button
                        data-testid="theme-toggle-btn-mobile"
                        onClick={toggleTheme}
                        aria-label="Alternar tema"
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-zinc-300/60 dark:border-white/10 text-zinc-700 dark:text-zinc-200"
                    >
                        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </button>
                    <button
                        data-testid="mobile-menu-btn"
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-zinc-300/60 dark:border-white/10 text-zinc-700 dark:text-white"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Abrir menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden border-t border-zinc-200 dark:border-white/5 bg-white/95 dark:bg-black/90 backdrop-blur-xl"
                >
                    <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <button
                                key={n.id}
                                data-testid={`mobile-nav-${n.id}-btn`}
                                onClick={() => go(n.id)}
                                className="text-left py-3 text-zinc-800 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-white"
                            >
                                {n.label}
                            </button>
                        ))}
                        <a
                            data-testid="mobile-nav-portal-colaborador-btn"
                            href={PORTAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#2DA89E]/50 text-[#2DA89E]"
                        >
                            <UserCircle className="w-4 h-4" />
                            Portal do Colaborador
                        </a>
                        <button
                            data-testid="mobile-nav-contato-btn"
                            onClick={() => go("contato")}
                            className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#E89546] text-black font-semibold"
                        >
                            Contato <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
